@if (isOpen) {
<div class="modal-backdrop" (click)="onBackdropClick($event)">
  <div class="modal-container">
    <div class="modal-header">
      <h2 class="modal-title">{{ title }}</h2>
      <button class="close-button" (click)="onClose()" aria-label="Close">
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>

    <!-- State: confirm-change - Confirmation before requesting status change -->
    @if (state === 'confirm-change') {
      <div class="modal-body">
        <p class="modal-message">
          Are you sure you would like to change the status of legal entity {{ customName }}?
        </p>

        <div class="status-transition">
          <div class="status-row">
            <az-tag [status]="getCurrentStatusPrimary()">{{ getCurrentStatusPrimary() }}</az-tag>
            <div class="status-text">{{ getCurrentStatusSecondary() }}</div>
          </div>

          <div class="arrow-down">
            <span class="material-symbols-outlined">arrow_downward</span>
          </div>

          <div class="status-row">
            <az-tag [status]="getNewStatusPrimary()">{{ getNewStatusPrimary() }}</az-tag>
            <div class="status-text">{{ getNewStatusSecondary() }}</div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn--reject" (click)="onCancelChange()">Cancel</button>
        <button class="btn btn--approve" (click)="onRequestChange()">Request Change</button>
      </div>
    }

    <!-- State: review - Original status change review -->
    @if (state === 'review') {
      <div class="modal-body">
        <p class="modal-message">
          The status of legal entity {{ customName }} has changed and is awaiting approval.
        </p>

        <div class="status-transition">
          <div class="status-row">
            <az-tag [status]="getCurrentStatusPrimary()">{{ getCurrentStatusPrimary() }}</az-tag>
            <div class="status-text">{{ getCurrentStatusSecondary() }}</div>
          </div>

          <div class="arrow-down">
            <span class="material-symbols-outlined">arrow_downward</span>
          </div>

          <div class="status-row">
            <az-tag [status]="getNewStatusPrimary()">{{ getNewStatusPrimary() }}</az-tag>
            <div class="status-text">{{ getNewStatusSecondary() }}</div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn--reject" (click)="onReject()">Reject</button>
        <button class="btn btn--approve" (click)="onApprove()">Approve</button>
      </div>
    }

    <!-- State: confirm-reject - Confirmation before rejecting -->
    @if (state === 'confirm-reject') {
      <div class="modal-body">
        <p class="modal-message modal-message--centered">
          Are you sure you want to reject the status change request for
          {{ customName }} ?
        </p>
      </div>

      <div class="modal-footer">
        <button class="btn btn--secondary" (click)="onCancelReject()">No</button>
        <button class="btn btn--approve" (click)="onConfirmReject()">Yes</button>
      </div>
    }

    <!-- State: success - Approval confirmed -->
    @if (state === 'success') {
      <div class="modal-body modal-body--result">
        <div class="result-icon result-icon--success">
          <span class="material-symbols-outlined">check_circle</span>
        </div>
        <h3 class="result-title">Success!</h3>
        <p class="result-message">The status of {{ customName }} has been changed.</p>
      </div>
    }

    <!-- State: success-with-message - Success with custom backend message -->
    @if (state === 'success-with-message') {
      <div class="modal-body modal-body--result">
        @if (warningMessage) {
          <div class="modal-warning">
            <span class="material-symbols-outlined modal-warning__icon">warning</span>
            <p class="modal-warning__message">
              {{ warningMessage }}
              @if (warningLinkText && warningLinkUrl) {
                <a
                  [href]="warningLinkUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="modal-warning__link"
                  >{{ warningLinkText }}</a
                >.
              }
            </p>
          </div>
        }
        <div class="result-icon result-icon--success">
          <span class="material-symbols-outlined">check_circle</span>
        </div>
        <h3 class="result-title">Success!</h3>
        <p class="result-message">{{ successMessage }}</p>
      </div>
    }

    <!-- State: request-sent - Status change request sent for approval -->
    @if (state === 'request-sent') {
      <div class="modal-body modal-body--result">
        <div class="result-icon result-icon--success">
          <span class="material-symbols-outlined">check_circle</span>
        </div>
        <h3 class="result-title">Success!</h3>
        <p class="result-message">
          The status change for {{ customName }} has been sent for approval.
        </p>
      </div>
    }

    <!-- State: rejected - Rejection confirmed -->
    @if (state === 'rejected') {
      <div class="modal-body modal-body--result">
        <div class="result-icon result-icon--rejected">
          <span class="material-symbols-outlined">cancel</span>
        </div>
        <h3 class="result-title">Rejected</h3>
        <p class="result-message">
          The status change request of {{ customName }} has been rejected.
        </p>
      </div>
    }

    <!-- State: processing - API call in progress -->
    @if (state === 'processing') {
      <div class="modal-body modal-body--result">
        <div class="result-icon">
          <span class="material-symbols-outlined spinning">progress_activity</span>
        </div>
        <h3 class="result-title">Processing...</h3>
        <p class="result-message">Please wait while we process your request.</p>
      </div>
    }

    <!-- State: already-processed - Status was already approved/rejected -->
    @if (state === 'already-processed') {
      <div class="modal-body">
        <p class="modal-message modal-message--centered">
          {{ alreadyProcessedBy }}
        </p>
      </div>
    }

    <!-- State: error - Error occurred during upload/validation -->
    @if (state === 'error') {
      <div class="modal-body modal-body--result">
        <div class="result-icon result-icon--error">
          <span class="material-symbols-outlined">error</span>
        </div>
        <h3 class="result-title">Error</h3>
        <p class="result-message">
          {{ errorMessage || 'An error occurred while processing your request. Please try again.' }}
        </p>
      </div>
    }

    <!-- State: custom-confirm - Custom confirmation with fully customizable text -->
    @if (state === 'custom-confirm') {
      <div class="modal-body">
        <p class="modal-message modal-message--centered">
          {{ customMessage }}
        </p>
      </div>

      <div class="modal-footer">
        <button class="btn btn--secondary" (click)="onCustomNo()">{{ customNoButtonLabel }}</button>
        <button class="btn btn--approve" (click)="onCustomYes()">{{ customYesButtonLabel }}</button>
      </div>
    }
  </div>
</div>
}
